import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function UseCases() {
  const cases = [
    {
      icon: 'ri-bank-card-line',
      title: 'Subscription & Billing Inquiries',
      description: 'Handle payment status, invoice requests, subscription changes, and billing cycle questions without human intervention.'
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'Account Access & Authentication',
      description: 'Resolve password resets, account verification, login issues, and permission requests through automated workflows.'
    },
    {
      icon: 'ri-truck-line',
      title: 'Order or Transaction Status',
      description: 'Provide real-time updates on order processing, delivery tracking, and transaction confirmation by querying internal systems.'
    },
    {
      icon: 'ri-question-answer-line',
      title: 'Feature & Usage Questions',
      description: 'Answer product functionality questions, usage limits, and feature availability based on customer tier and configuration.'
    },
    {
      icon: 'ri-stack-line',
      title: 'High-Volume Repetitive Categories',
      description: 'Automate any support category with predictable patterns and clear resolution paths that consume agent time.'
    },
    {
      icon: 'ri-links-line',
      title: 'System Integration Requests',
      description: 'Handle API access requests, webhook configuration, and integration setup guidance through structured processes.'
    }
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, getItemStyle } = useStaggerAnimation(cases.length, { threshold: 0.08 });

  return (
    <section id="use-cases" className="bg-[#F9F8F6] py-16 md:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <div
          ref={headerRef}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B8985F]/10 mb-5 md:mb-6">
              <span className="text-[12px] tracking-[1.5px] uppercase text-[#B8985F] font-semibold">Use Cases</span>
            </div>
            <h2 className="text-[28px] md:text-[38px] font-bold text-[#1A1A1A] mb-3">Where Agents Reduce Volume</h2>
            <p className="text-[14px] md:text-[16px] text-[#777] max-w-[500px]">Specific support categories where AI agents deliver measurable impact for companies.</p>
          </div>
          <Link
            to="/book-a-call"
            className="px-6 py-3 bg-[#B8985F] text-white text-[14px] font-semibold rounded-md hover:bg-[#A68850] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 self-start md:self-auto"
          >
            Discuss Your Use Case
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {cases.map((useCase, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-5 md:p-7 border border-[#F0EDE6] hover:border-[#B8985F]/30 hover:shadow-[0_8px_30px_rgba(184,152,95,0.08)] transition-all duration-300 cursor-pointer group"
              style={getItemStyle(index)}
            >
              <div className="w-11 h-11 rounded-lg bg-[#B8985F]/10 flex items-center justify-center mb-4 md:mb-5 group-hover:bg-[#B8985F]/20 transition-colors">
                <i className={`${useCase.icon} text-[#B8985F] text-lg`}></i>
              </div>
              <h3 className="text-[15px] md:text-[16px] font-semibold text-[#1A1A1A] mb-2">{useCase.title}</h3>
              <p className="text-[13px] md:text-[14px] text-[#888] leading-[1.6]">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
